import { FC, useEffect, useMemo, useRef } from 'react';
import { StringInputViewModel } from '@sitecore-jss/sitecore-jss-forms';
import { FieldChangeCallback, ValueFieldProps } from '@sitecore-jss/sitecore-jss-react-forms';

type FieldConfig = {
  queryParam: string;
  normalize?: (value: string) => string | null;
};

const normalizeBooleanValue = (value: string): string | null => {
  const normalizedValue = value.trim().toLowerCase();

  if (['true', '1', 'yes', 'y', 'on'].includes(normalizedValue)) {
    return 'TRUE';
  }

  if (['false', '0', 'no', 'n', 'off'].includes(normalizedValue)) {
    return 'FALSE';
  }

  return null;
};

const FIELD_CONFIG: Record<string, FieldConfig> = {
  gnh_appname: { queryParam: 'appname' },
  gnh_appversion: { queryParam: 'appver' },
  gnh_vid: { queryParam: 'vid' },
  gnh_pid: { queryParam: 'pid' },
  gnh_esn: { queryParam: 'esn' },
  gnh_firmware_version: { queryParam: 'firmware' },
  gnh_url_productname: { queryParam: 'prodname' },
  gnh_skipprodreg: { queryParam: 'skipProdReg', normalize: normalizeBooleanValue },
  gnh_os: { queryParam: 'os' },
  gnh_osversion: { queryParam: 'osver' },
};

const getHiddenValue = (
  fieldName: string,
  queryParameterValue: string | null,
  fallbackValue: string
): string => {
  if (!queryParameterValue) {
    return fallbackValue;
  }

  const normalize = FIELD_CONFIG[fieldName]?.normalize;

  return normalize ? (normalize(queryParameterValue) ?? fallbackValue) : queryParameterValue;
};

const getQueryParameterValue = (searchString: string, fieldName: string): string | null => {
  const queryParam = FIELD_CONFIG[fieldName]?.queryParam;

  if (!queryParam || !searchString) {
    return null;
  }

  return new URLSearchParams(searchString).get(queryParam);
};

const HiddenText: FC<ValueFieldProps<StringInputViewModel>> = ({ field, onChange, value }) => {
  const { id, name } = field.valueField;
  const model = field.model as StringInputViewModel;

  const searchString = useMemo(() => {
    if (typeof window === 'undefined') return '';
    try {
      return window.parent.location.search;
    } catch {
      return window.location.search;
    }
  }, []);

  const queryParameterValue = getQueryParameterValue(searchString, model.name);

  const hiddenValue = getHiddenValue(model.name, queryParameterValue, value ?? '');

  const onChangeRef = useRef<FieldChangeCallback>(onChange);

  useEffect(() => {
    onChangeRef.current(name, hiddenValue, true, []);
  }, [hiddenValue, name]);

  return (
    <input
      type="hidden"
      className={model.cssClass}
      id={id}
      name={name}
      value={hiddenValue}
      maxLength={model.maxLength}
      placeholder={model.placeholderText}
      readOnly
    />
  );
};

export default HiddenText;
